import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import api from '../services/api';
import { getClasseLabel } from '../utils/classeUtils';
import { useAcademicYear } from '../contexts/AcademicYearContext';

const getOuvertureId = (oc) => oc.ouverture_classe_id || oc.id;

const ParentNotifyBulletinsPage = () => {
  const { currentYear, loading: yearLoading } = useAcademicYear();
  const [ouvertures, setOuvertures] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  const [ouvertureClasseId, setOuvertureClasseId] = useState('');
  const [periodeId, setPeriodeId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentYear?.id) {
      setOuvertures([]);
      setPeriodes([]);
      setOuvertureClasseId('');
      setPeriodeId('');
      return;
    }

    const anneeId = currentYear.id;

    Promise.all([
      api.get('/ouvertures-classes', { params: { annee_scolaire_id: anneeId } }),
      api.get('/periodes'),
    ])
      .then(([ouvRes, periodesRes]) => {
        setOuvertures(ouvRes.data.success ? (ouvRes.data.data || []) : []);
        setPeriodes(periodesRes.data.success ? (periodesRes.data.data || []) : []);
      })
      .catch(() => {
        setOuvertures([]);
        setPeriodes([]);
      });

    setOuvertureClasseId('');
    setPeriodeId('');
  }, [currentYear]);

  const handleNotify = async () => {
    if (!ouvertureClasseId || !periodeId) {
      toast.error('Sélectionnez une classe et une période');
      return;
    }

    const result = await Swal.fire({
      title: 'Notifier les parents ?',
      text: 'Un email et une notification seront envoyés aux parents ayant un compte actif.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Envoyer',
    });
    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const res = await api.post('/parent-portal/notify-bulletins', {
        ouverture_classe_id: parseInt(ouvertureClasseId, 10),
        periode_id: parseInt(periodeId, 10),
      });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-lg">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Notifier les parents — Bulletins</h1>
      <p className="text-sm text-gray-500 mb-2">
        Informez les parents par email et notification in-app que les bulletins sont disponibles.
      </p>
      {currentYear && (
        <p className="text-sm text-primary-700 font-medium mb-6">
          Année scolaire en cours : {currentYear.annee_scolaire}
        </p>
      )}
      {!yearLoading && !currentYear && (
        <p className="text-sm text-amber-600 mb-6">Aucune année scolaire en cours n'est définie.</p>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Classe ouverte</label>
          <select
            value={ouvertureClasseId}
            onChange={(e) => setOuvertureClasseId(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="">— Choisir —</option>
            {ouvertures.map((oc) => (
              <option key={getOuvertureId(oc)} value={getOuvertureId(oc)}>
                {getClasseLabel(oc, oc.nom_classe)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Période</label>
          <select
            value={periodeId}
            onChange={(e) => setPeriodeId(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="">— Choisir —</option>
            {periodes.map((p) => (
              <option key={p.id} value={p.id}>{p.periode}</option>
            ))}
          </select>
        </div>
        <button
          onClick={handleNotify}
          disabled={loading || yearLoading || !currentYear}
          className="w-full py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
        >
          {loading ? 'Envoi en cours...' : 'Notifier les parents'}
        </button>
      </div>
    </div>
  );
};

export default ParentNotifyBulletinsPage;
